/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectSome,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";

export const LINK_EXTERNAL_ID_DISCRIMINATOR = new Uint8Array([
  156, 102, 141, 155, 175, 13, 29, 86,
]);

export function getLinkExternalIdDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    LINK_EXTERNAL_ID_DISCRIMINATOR,
  );
}

export type LinkExternalIdInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountAgentAccount extends string | AccountMeta<string> = string,
  TAccountExternalIdMapping extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    "11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountAgentAccount extends string
        ? WritableAccount<TAccountAgentAccount>
        : TAccountAgentAccount,
      TAccountExternalIdMapping extends string
        ? WritableAccount<TAccountExternalIdMapping>
        : TAccountExternalIdMapping,
      TAccountAuthority extends string
        ? WritableSignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type LinkExternalIdInstructionData = {
  discriminator: ReadonlyUint8Array;
  platform: string;
  externalId: string;
  verified: boolean;
};

export type LinkExternalIdInstructionDataArgs = {
  platform: string;
  externalId: string;
  verified: boolean;
};

export function getLinkExternalIdInstructionDataEncoder(): Encoder<LinkExternalIdInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["platform", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["externalId", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["verified", getBooleanEncoder()],
    ]),
    (value) => ({ ...value, discriminator: LINK_EXTERNAL_ID_DISCRIMINATOR }),
  );
}

export function getLinkExternalIdInstructionDataDecoder(): Decoder<LinkExternalIdInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["platform", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["externalId", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["verified", getBooleanDecoder()],
  ]);
}

export function getLinkExternalIdInstructionDataCodec(): Codec<
  LinkExternalIdInstructionDataArgs,
  LinkExternalIdInstructionData
> {
  return combineCodec(
    getLinkExternalIdInstructionDataEncoder(),
    getLinkExternalIdInstructionDataDecoder(),
  );
}

export type LinkExternalIdAsyncInput<
  TAccountAgentAccount extends string = string,
  TAccountExternalIdMapping extends string = string,
  TAccountAuthority extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  agentAccount: Address<TAccountAgentAccount>;
  externalIdMapping?: Address<TAccountExternalIdMapping>;
  authority: TransactionSigner<TAccountAuthority>;
  systemProgram?: Address<TAccountSystemProgram>;
  platform: LinkExternalIdInstructionDataArgs["platform"];
  externalId: LinkExternalIdInstructionDataArgs["externalId"];
  verified: LinkExternalIdInstructionDataArgs["verified"];
};

export async function getLinkExternalIdInstructionAsync<
  TAccountAgentAccount extends string,
  TAccountExternalIdMapping extends string,
  TAccountAuthority extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: LinkExternalIdAsyncInput<
    TAccountAgentAccount,
    TAccountExternalIdMapping,
    TAccountAuthority,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  LinkExternalIdInstruction<
    TProgramAddress,
    TAccountAgentAccount,
    TAccountExternalIdMapping,
    TAccountAuthority,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agentAccount: { value: input.agentAccount ?? null, isWritable: true },
    externalIdMapping: {
      value: input.externalIdMapping ?? null,
      isWritable: true,
    },
    authority: { value: input.authority ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.externalIdMapping.value) {
    accounts.externalIdMapping.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([101, 120, 116, 101, 114, 110, 97, 108, 95, 105, 100]),
        ),
        getUtf8Encoder().encode(expectSome(args.platform)),
        getUtf8Encoder().encode(expectSome(args.externalId)),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agentAccount),
      getAccountMeta(accounts.externalIdMapping),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getLinkExternalIdInstructionDataEncoder().encode(
      args as LinkExternalIdInstructionDataArgs,
    ),
    programAddress,
  } as LinkExternalIdInstruction<
    TProgramAddress,
    TAccountAgentAccount,
    TAccountExternalIdMapping,
    TAccountAuthority,
    TAccountSystemProgram
  >);
}

export type LinkExternalIdInput<
  TAccountAgentAccount extends string = string,
  TAccountExternalIdMapping extends string = string,
  TAccountAuthority extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  agentAccount: Address<TAccountAgentAccount>;
  externalIdMapping: Address<TAccountExternalIdMapping>;
  authority: TransactionSigner<TAccountAuthority>;
  systemProgram?: Address<TAccountSystemProgram>;
  platform: LinkExternalIdInstructionDataArgs["platform"];
  externalId: LinkExternalIdInstructionDataArgs["externalId"];
  verified: LinkExternalIdInstructionDataArgs["verified"];
};

export function getLinkExternalIdInstruction<
  TAccountAgentAccount extends string,
  TAccountExternalIdMapping extends string,
  TAccountAuthority extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: LinkExternalIdInput<
    TAccountAgentAccount,
    TAccountExternalIdMapping,
    TAccountAuthority,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): LinkExternalIdInstruction<
  TProgramAddress,
  TAccountAgentAccount,
  TAccountExternalIdMapping,
  TAccountAuthority,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agentAccount: { value: input.agentAccount ?? null, isWritable: true },
    externalIdMapping: {
      value: input.externalIdMapping ?? null,
      isWritable: true,
    },
    authority: { value: input.authority ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agentAccount),
      getAccountMeta(accounts.externalIdMapping),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getLinkExternalIdInstructionDataEncoder().encode(
      args as LinkExternalIdInstructionDataArgs,
    ),
    programAddress,
  } as LinkExternalIdInstruction<
    TProgramAddress,
    TAccountAgentAccount,
    TAccountExternalIdMapping,
    TAccountAuthority,
    TAccountSystemProgram
  >);
}

export type ParsedLinkExternalIdInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    agentAccount: TAccountMetas[0];
    externalIdMapping: TAccountMetas[1];
    authority: TAccountMetas[2];
    systemProgram: TAccountMetas[3];
  };
  data: LinkExternalIdInstructionData;
};

export function parseLinkExternalIdInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedLinkExternalIdInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 4) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      agentAccount: getNextAccount(),
      externalIdMapping: getNextAccount(),
      authority: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getLinkExternalIdInstructionDataDecoder().decode(instruction.data),
  };
}
